import { supabase } from '@/integrations/supabase/client';

export interface ProfessionService {
  id: string;           // UUID
  specializationId: string; // UUID
  serviceId: number;    // Integer ID
  label: string;        // Hebrew
  label_en?: string;    // English
  label_fr?: string;    // French
  label_ru?: string;    // Russian
  displayOrder: number;
}

// Cache for services by specialization UUID
const servicesCache: Map<string, ProfessionService[]> = new Map();
// Cache for services by profession UUID (aggregated from all specializations)
const professionServicesCache: Map<string, ProfessionService[]> = new Map();

/**
 * Get services for a specific specialization UUID
 */
export const getServicesForSpecialization = async (
  specializationId: string
): Promise<ProfessionService[]> => {
  // Check cache first
  if (servicesCache.has(specializationId)) {
    return servicesCache.get(specializationId)!;
  }

  try {
    const { data, error } = await supabase
      .from('profession_services')
      .select('id, specialization_id, service_id, label, label_en, label_fr, label_ru, display_order')
      .eq('specialization_id', specializationId)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error || !data) {
      console.error('Error fetching services for specialization:', error);
      return [];
    }

    const services: ProfessionService[] = data.map(s => ({
      id: s.id,
      specializationId: s.specialization_id,
      serviceId: s.service_id,
      label: s.label,
      label_en: s.label_en,
      label_fr: s.label_fr,
      label_ru: s.label_ru,
      displayOrder: s.display_order
    }));

    // Cache the result
    servicesCache.set(specializationId, services);
    return services;
  } catch (error) {
    console.error('Error in getServicesForSpecialization:', error);
    return [];
  }
};

/**
 * Get all services for a main profession UUID
 * Fetches services from all specializations under this profession
 */
export const getServicesForProfession = async (
  professionId: string
): Promise<ProfessionService[]> => {
  // Check cache first
  if (professionServicesCache.has(professionId)) {
    return professionServicesCache.get(professionId)!;
  }

  try {
    // First, get all specialization IDs for this profession
    const { data: specs, error: specsError } = await supabase
      .from('profession_specializations')
      .select('id')
      .eq('profession_id', professionId)
      .eq('is_active', true);

    if (specsError || !specs || specs.length === 0) {
      console.log('No specializations found for profession:', professionId);
      return [];
    }

    const specIds = specs.map(s => s.id);

    // Fetch all services for these specializations
    const { data, error } = await supabase
      .from('profession_services')
      .select('id, specialization_id, service_id, label, label_en, label_fr, label_ru, display_order')
      .in('specialization_id', specIds)
      .eq('is_active', true)
      .order('display_order', { ascending: true });

    if (error || !data) {
      console.error('Error fetching services for profession:', error);
      return [];
    }

    const services: ProfessionService[] = data.map(s => ({
      id: s.id,
      specializationId: s.specialization_id,
      serviceId: s.service_id,
      label: s.label,
      label_en: s.label_en,
      label_fr: s.label_fr,
      label_ru: s.label_ru,
      displayOrder: s.display_order
    }));

    // Cache the result
    professionServicesCache.set(professionId, services);
    
    // Also cache individual specialization results
    specIds.forEach(specId => {
      const specServices = services.filter(s => s.specializationId === specId);
      servicesCache.set(specId, specServices);
    });

    return services;
  } catch (error) {
    console.error('Error in getServicesForProfession:', error);
    return [];
  }
};

/**
 * Get translated label based on current language
 * Falls back to Hebrew if translation not available
 */
export const getServiceLabel = (
  service: ProfessionService,
  language: string
): string => {
  switch (language) {
    case 'en':
      return service.label_en || service.label;
    case 'fr':
      return service.label_fr || service.label;
    case 'ru':
      return service.label_ru || service.label;
    default:
      return service.label;
  }
};

/**
 * Clear the services cache (useful for testing or force refresh)
 */
export const clearServicesCache = (): void => {
  servicesCache.clear();
  professionServicesCache.clear();
};
